import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useWindowDimensions from 'customHook/useWindowDimensions';
import FooterBar from 'components/FooterBar';
import Spinner from 'components/Spinner';
import SubHeader from './components/Timeline/SubHeader';
import 'react-calendar-timeline/lib/Timeline.css';
import useTimeline from 'customHook/useTimeline';
import TimelineTable from 'components/TimelineTable';
import ItemRenderer from './components/Timeline/ItemRenderer';
import GroupLabel from './components/Timeline/GroupLabel';
import ClientModal from './components/ClientModal';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import {
  GET_ALL_VALID_BOOKINGS,
  GET_ALL_ROOMS,
  UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID,
  GET_BOOKING_DETAILS_BY_BOOKING_ID
} from 'graphql/service';
import { SideBarItemContainer } from './styles';
import { scrollDown } from 'utils/scrollDown';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { retry } from "utils/util";
import moment from 'moment-timezone';
import { TIME_ZONE } from 'constants/constant';

const Bookings = () => {
  
  const location = useLocation()
  const history = useHistory()
  const roomRef = useRef()
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [validRooms, setValidRooms] = useState([])
  const [validBookings, setValidBookings] = useState([])
  const [bookingDetailsData, setBookingDetailsData] = useState({})
  const { data: roomsData, loading, error } = useQuery(GET_ALL_ROOMS, {
    fetchPolicy: 'cache-first',
    onCompleted: (roomsData) => {
      if(roomsData.validRooms) {
        setValidRooms(roomsData.validRooms || [])
      }
    }
  });

  const [getValidBookingsByDateRange,{ loading: bookingLoading, error: bookingError, fetchMore }] = useLazyQuery(
    GET_ALL_VALID_BOOKINGS,
    {
      fetchPolicy: 'cache-first',
      onCompleted: (bookingsData) => {
        if(bookingsData.validBookingsPaginated) {
          setValidBookings(bookingsData.validBookingsPaginated.items || [])
        }
      }
    }
  );

  const pickedDate = useSelector(state => state.pickedDateReducer.pickedDate)
  const { width } = useWindowDimensions();
  const columnNumber = Math.floor(width/120)

  useEffect(() => {
    console.log('effect fired, pickedDate:', pickedDate)
    if(pickedDate) {
      getValidBookingsByDateRange({
        variables: {
          variables: {
            filters: {
              dateFrom: moment(pickedDate).tz(TIME_ZONE).startOf('week').subtract(columnNumber, 'day').subtract(-columnNumber, 'day').toDate(),
              dateTo: moment(pickedDate).tz(TIME_ZONE).startOf('week').add(2*columnNumber, 'day').toDate()
            },
            pagination: {
              limit: 1000,
              offset: 0
            }
          },
        }
      })
    }
  }, [getValidBookingsByDateRange, pickedDate, columnNumber])

  const { timelineState, groups, setGroups, items, setItems } = useTimeline(validRooms, validBookings);

  const scrollToRoom = () => scrollDown(roomRef.current?.offsetTop, 1000)
  
  const [getBookingDetailsByBookingId] = useLazyQuery(
    GET_BOOKING_DETAILS_BY_BOOKING_ID,
    {
      onCompleted: (data) => {
        setBookingDetailsData(data)
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message
          if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
          }
          debouncedErrorMessage(content);
        }
      },
    }
  )

  useEffect(() => {
    if(bookingId.length>0) {
      getBookingDetailsByBookingId({
        variables: {
          bookingId
        }
      })
    }
  }, [bookingId, getBookingDetailsByBookingId])

  const [updateRoomBooking] = useMutation(
    UPDATE_BOOKING_ROOM_BY_BOOKING_ID_AND_ROOM_ID,
    { 
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message
          if(error.message.includes('Validation')) {
              content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
          }
          debouncedErrorMessage(content);
        }
      }
    } 
  )

  useEffect(() => {
    if(roomRef.current) {
      scrollToRoom() 
    }
  }, [])

  const handleItemClick = (itemId, e, time) => {
    const bookingId = itemId?.split('/')[0]
    setClientModalOpen(true)
    setBookingId(bookingId)
  }

  const handleItemDoubleClick = (itemId, e, time) => {
    const groupId = itemId?.split('/')[1]
    history.push(`/booking-details/${groupId}`)
  }

  if (loading || bookingLoading) return <Spinner />
  if (error || bookingError) return <div>error</div>

  let SideBarItem = []
  let GroupLabels = []
  let top = 152
  for(const key in groups) {
    const group = groups[key].map((groupItem) => {
      return Object.assign({}, groupItem, {
        title: (
          <SideBarItemContainer ref={groupItem.id===location.state?.roomNumber? roomRef: null} warning={groupItem.status==='OUT_OF_ORDER'}>
            {groupItem.id}
            <div className={groupItem.status==='OUT_OF_ORDER'? 'warningCover': null}></div>
          </SideBarItemContainer>
        )
      })
    })
    SideBarItem = SideBarItem.concat(group)
    GroupLabels.push(
      <GroupLabel key={key} groupName={key} height={groups[key].length * 32 - 1} top={top}/>
    )
    top += groups[key].length * 32
  }

  const moveResizeValidator = (action, item, time, resizeEdge) => {
    if (action === 'move') {
      return item.start; // freeze time on group‑moves
    }
    return time;
  };

  const handleItemMove = async (itemId, dragTime, newGroupOrder) => {
    const originItems = [...items]
    const newItems = originItems.filter(i => i.id !== itemId)
    let item = originItems.find((item) => item.id === itemId);
    const prevGroupIndex = SideBarItem.findIndex(g => g.id === item.group);
    const label = Object.entries(groups).find(([key, arr]) =>
      arr.some(group => group.id === item.group)
    )?.[0];
    const roomTypeName = SideBarItem[newGroupOrder].roomTypeName
    const bookingId = itemId?.split('/')[0]
    const roomId = SideBarItem[newGroupOrder].roomId
    if(newGroupOrder === prevGroupIndex) return
    if(label !== roomTypeName) {
      debouncedErrorMessage('Cannot move booking to another room type')
      return
    }
    item.group = SideBarItem[newGroupOrder].id
    newItems.push(item)
    setItems(newItems)
    setBookingDetailsData((prev) => ({
      ...prev,
      bookingByID: {
        ...prev.bookingByID,
        roomBookings: [{...prev.bookingByID?.roomBookings?.[0], room: {...prev.bookingByID?.roomBookings?.[0]?.room, roomNumber: SideBarItem[newGroupOrder].id}},...prev.bookingByID?.roomBookings]
      }
    }))
    try {
      await retry (() => updateRoomBooking({
        variables: {
          input: {
            roomId,
            bookingId
          },
        },
      }));
    } catch (error) {
      // Revert the change in case of an error
      setItems(originItems);
      setBookingDetailsData((prev) => ({
        ...prev,
        bookingByID: {
          ...prev.bookingByID,
          roomBookings: [...prev.bookingByID?.roomBookings].slice(1)
        }
      }))
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>Bookings - Rook Hotel PMS</title>
        <meta name="description" content="Bookings - Rook Hotel PMS" />
      </Helmet>
      <SubHeader timelineState={timelineState} />
      <TimelineTable
        timelineState={timelineState}
        SideBarItem={SideBarItem}
        items={items}
        setItems={setItems}
        itemRenderer={ItemRenderer}
        ClientModal={ClientModal}
        bookingDetailsData={bookingDetailsData}
        setBookingDetailsData={setBookingDetailsData}
        moveResizeValidator={moveResizeValidator}
        handleItemClick={handleItemClick}
        handleItemDoubleClick={handleItemDoubleClick}
        handleItemMove={handleItemMove}
        clientModalOpen={clientModalOpen}
        setClientModalOpen={setClientModalOpen}
        fixedHeader
      />
      {GroupLabels}
      <FooterBar />
    </>
  );
};

export default Bookings;
